import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/utils/trpc';
import type { RouterOutput } from '@trapeze/server';
import { AlertTriangle, Copy, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

type Provider = RouterOutput['admin']['getProvidersWithAppointmentTypes']['providers'][number];

interface CopyProviderAppointmentTypesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetProvider: Provider | null;
  allProviders: Provider[];
  organizationId: string;
  onSuccess: () => void;
  // Mapping of provider ID to appointment type count
  getProviderTypeCount: (providerId: string) => number;
}

export function CopyProviderAppointmentTypesDialog({
  open,
  onOpenChange,
  targetProvider,
  allProviders,
  organizationId,
  onSuccess,
  getProviderTypeCount,
}: CopyProviderAppointmentTypesDialogProps) {
  const [sourceProviderId, setSourceProviderId] = useState('');

  // Reset selection when dialog opens/closes or target changes
  useEffect(() => {
    if (!open || !targetProvider) {
      setSourceProviderId('');
    }
  }, [open, targetProvider]);

  // Copy mutation
  const copyMutation = trpc.admin.copyProviderAppointmentTypes.useMutation({
    onSuccess: (data) => {
      toast.success(`Successfully copied ${data.copiedCount} appointment types`);
      onSuccess();
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(`Failed to copy appointment types: ${error.message}`);
    },
  });

  const handleCopy = () => {
    if (!targetProvider || !sourceProviderId) return;

    copyMutation.mutate({
      organizationId,
      sourceProviderId,
      targetProviderIds: [targetProvider.id],
      mode: 'replace', // Default to replace as per UI description
    });
  };

  // Filter out the target provider from source options
  const availableSourceProviders = allProviders.filter(
    (p) => p.id !== targetProvider?.id && getProviderTypeCount(p.id) > 0
  );

  const selectedSourceProvider = allProviders.find((p) => p.id === sourceProviderId);
  const targetTypeCount = targetProvider ? getProviderTypeCount(targetProvider.id) : 0;

  if (!targetProvider) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-5 w-5" />
            Copy Appointment Types to {targetProvider.name}
          </DialogTitle>
          <DialogDescription>
            Copy all appointment types from another provider. This will replace all existing
            appointment types for {targetProvider.name}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning if target has existing types */}
          {targetTypeCount > 0 && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-900">
                <p className="font-medium">Warning</p>
                <p>
                  {targetProvider.name} currently has {targetTypeCount}{' '}
                  {targetTypeCount === 1 ? 'appointment type' : 'appointment types'}. These will be
                  removed and replaced with the copied types.
                </p>
              </div>
            </div>
          )}

          {/* Source Provider Selection */}
          <div className="space-y-2">
            <Label htmlFor="sourceProvider">Copy types from</Label>
            {availableSourceProviders.length === 0 ? (
              <div className="text-sm text-gray-500 p-3 bg-gray-50 rounded-md">
                No other providers with appointment types available
              </div>
            ) : (
              <>
                <Select value={sourceProviderId} onValueChange={setSourceProviderId}>
                  <SelectTrigger id="sourceProvider">
                    <SelectValue placeholder="Select a provider" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSourceProviders.map((provider) => {
                      const count = getProviderTypeCount(provider.id);
                      return (
                        <SelectItem key={provider.id} value={provider.id}>
                          <div className="flex items-center justify-between gap-3 w-full">
                            <span>{provider.name}</span>
                            <Badge variant="secondary" className="ml-2">
                              {count} {count === 1 ? 'type' : 'types'}
                            </Badge>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>

                {/* Preview */}
                {selectedSourceProvider && (
                  <div className="text-xs text-gray-600 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="font-medium text-blue-900 mb-1">Will copy:</p>
                    <p>
                      {getProviderTypeCount(selectedSourceProvider.id)}{' '}
                      {getProviderTypeCount(selectedSourceProvider.id) === 1 ? 'type' : 'types'}{' '}
                      from {selectedSourceProvider.name} to {targetProvider.name}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Error Message */}
          {copyMutation.isError && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200">
              {copyMutation.error.message}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={copyMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCopy}
            disabled={
              !sourceProviderId || copyMutation.isPending || availableSourceProviders.length === 0
            }
          >
            {copyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Copy Types
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
